import api from './api';

/*
 * API client for pets — login-less wish-list owners managed by a caretaker.
 *
 * A pet has no email/login. It exists only to give a wish a non-user display
 * identity: the wish still belongs to the caretaker (its `user_id`), but carries
 * a nullable `pet_id`, and every wish read payload gains a nullable
 *   pet: { id, name, species, avatar_url, avatar_color }
 * which clients render as the wish's owner in place of `wish.user`.
 *
 * Endpoints (see ../listful-api/docs/PETS_PLAN.md "Client contract"):
 *   GET    /groups/{group}/pets   list pets in a group (any member)
 *   POST   /groups/{group}/pets   create a pet (caller becomes the caretaker)
 *   GET    /pets/{pet}            pet detail
 *   PUT    /pets/{pet}            update  (caretaker only)
 *   DELETE /pets/{pet}            delete  (caretaker only; its wishes cascade away)
 *   GET    /pets/{pet}/wishes     the pet's wish list
 *
 * Each function returns the already-unwrapped payload: the shared axios
 * instance (`services/api.js`) strips the `{ success, data, ... }` envelope, so
 * `res.data` here is the plain object/array the controller returned.
 */

// GET /groups/{groupId}/pets — every pet in a group (any member may view).
export async function listGroupPets(groupId) {
    const res = await api.get(`/groups/${groupId}/pets`);
    return res.data;
}

// POST /groups/{groupId}/pets — add a pet to the group. The caller becomes its
// caretaker (the API sets owner_user_id from the authenticated user).
// `payload` = { name, species, avatar_color, birthday } (all but name optional).
export async function createPet(groupId, payload) {
    const res = await api.post(`/groups/${groupId}/pets`, payload);
    return res.data;
}

// GET /pets/{petId} — pet detail (includes the caretaker as `owner`).
export async function getPet(petId) {
    const res = await api.get(`/pets/${petId}`);
    return res.data;
}

// PUT /pets/{petId} — update the pet. Caretaker only (the API returns 403 otherwise).
export async function updatePet(petId, payload) {
    const res = await api.put(`/pets/${petId}`, payload);
    return res.data;
}

// DELETE /pets/{petId} — remove the pet. Caretaker only. Deleting a pet also
// removes its wishes (wishes.pet_id cascades on the server).
export async function deletePet(petId) {
    await api.delete(`/pets/${petId}`);
}

// GET /pets/{petId}/wishes — the pet's wish list. Owner-blindness still applies
// per viewer (the caretaker is masked), so the pet's gifts stay a surprise.
export async function getPetWishes(petId) {
    const res = await api.get(`/pets/${petId}/wishes`);
    return res.data;
}
